import { Header, Sidebar } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Calendar, ChevronRight, Mail, MoreHorizontal, Phone, Search, User, UserPlus } from 'lucide-react';

const MOCK_PATIENTS_LIST = [
  {
    id: '1',
    name: 'Sigríður Jónsdóttir',
    ssn: '120585-4929',
    dob: '12.05.1985',
    email: 'sigridur@example.is',
    phone: '888-1234',
    lastVisit: '30.12.2025',
  },
  {
    id: '2',
    name: 'Gunnar Helgason',
    ssn: '240890-3129',
    dob: '24.08.1990',
    email: 'gunnar@example.is',
    phone: '888-5678',
    lastVisit: '30.12.2025',
  },
  {
    id: '3',
    name: 'Helga Björnsdóttir',
    ssn: '150378-2239',
    dob: '15.03.1978',
    email: 'helga@example.is',
    phone: '888-4321',
    lastVisit: '29.12.2025',
  },
  {
    id: '4',
    name: 'Jóhannes Sveinsson',
    ssn: '010170-4459',
    dob: '01.01.1970',
    email: 'johannes@example.is',
    phone: '888-8765',
    lastVisit: '28.12.2025',
  },
  {
    id: '5',
    name: 'Arnar Jónsson',
    ssn: '101080-3349',
    dob: '10.10.1980',
    email: 'arnar@example.is',
    phone: '888-1111',
    lastVisit: '20.12.2025',
  },
  {
    id: '6',
    name: 'Kristín Ólafsdóttir',
    ssn: '050595-2219',
    dob: '05.05.1995',
    email: 'kristin@example.is',
    phone: '888-2222',
    lastVisit: '15.12.2025',
  },
];

export default function PatientsPage() {
  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="Sjúklingar">
          <Button className="gap-2">
            <UserPlus className="h-4 w-4" />
            Nýr sjúklingur
          </Button>
        </Header>

        <main className="flex-1 overflow-hidden bg-slate-50/30">
          <ScrollArea className="h-full">
            <div className="mx-auto max-w-6xl p-8">
              <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-center">
                <div>
                  <h2 className="font-bold text-2xl text-slate-900">Sjúklingaskrá</h2>
                  <p className="text-slate-500">Umsjón með öllum skráðum sjúklingum og sjúkrasögu þeirra.</p>
                </div>
                <div className="relative w-full md:w-96">
                  <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-slate-400" />
                  <Input placeholder="Leita að nafni eða kennitölu..." className="border-slate-200 bg-white pl-10" />
                </div>
              </div>

              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-left">
                      <thead>
                        <tr className="border-slate-100 border-b bg-slate-50/50">
                          <th className="px-6 py-4 font-semibold text-slate-500 text-xs uppercase tracking-wider">
                            Sjúklingur
                          </th>
                          <th className="px-6 py-4 font-semibold text-slate-500 text-xs uppercase tracking-wider">
                            Kennitala
                          </th>
                          <th className="px-6 py-4 font-semibold text-slate-500 text-xs uppercase tracking-wider">
                            Tengiliðaupplýsingar
                          </th>
                          <th className="px-6 py-4 font-semibold text-slate-500 text-xs uppercase tracking-wider">
                            Síðasta koma
                          </th>
                          <th className="px-6 py-4 font-semibold text-slate-500 text-xs uppercase tracking-wider" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {MOCK_PATIENTS_LIST.map((patient) => (
                          <tr key={patient.id} className="group cursor-pointer transition-colors hover:bg-slate-50/50">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                                  <User className="h-5 w-5" />
                                </div>
                                <div>
                                  <p className="font-semibold text-slate-900">{patient.name}</p>
                                  <p className="text-slate-500 text-xs">{patient.dob}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="font-mono text-slate-600 text-sm">{patient.ssn}</span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col gap-1 text-slate-500 text-xs">
                                <div className="flex items-center gap-1.5">
                                  <Mail className="h-3 w-3" />
                                  {patient.email}
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Phone className="h-3 w-3" />
                                  {patient.phone}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-1.5 text-slate-600 text-sm">
                                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                {patient.lastVisit}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 opacity-0 group-hover:opacity-100"
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem>Skoða sjúkrasögu</DropdownMenuItem>
                                    <DropdownMenuItem>Breyta upplýsingum</DropdownMenuItem>
                                    <DropdownMenuItem className="text-destructive">Eyða</DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <ChevronRight className="h-4 w-4 text-slate-300" />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </main>
      </div>
    </div>
  );
}
